<template>
    <el-dialog
        v-model="visible"
        :append-to-body="true"
        :close-on-click-modal="false"
        width="70%"
        class="image-dialog"
    >
        <div class="image-preview-container">
            <div class="zoom-controls">
                <el-button-group>
                    <el-button
                        @click="zoomOut"
                        :disabled="scale <= minScale"
                    >
                        <el-icon><Remove /></el-icon>
                    </el-button>
                    <el-button @click="resetZoom">{{ Math.round(scale * 100) }}%</el-button>
                    <el-button
                        @click="zoomIn"
                        :disabled="scale >= maxScale"
                    >
                        <el-icon><Plus /></el-icon>
                    </el-button>
                </el-button-group>
            </div>
            <div
                class="image-container"
                @wheel.prevent="handleWheel"
                @mousedown="startDrag"
                @mousemove="onDrag"
                @mouseup="stopDrag"
                @mouseleave="stopDrag"
            >
                <img
                    ref="imageRef"
                    :src="src"
                    :style="{
                        transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                        cursor: isDragging ? 'grabbing' : 'grab'
                    }"
                    @load="onImageLoad"
                />
            </div>
        </div>
    </el-dialog>
</template>

<script setup>
import { ref, reactive, watch } from "vue";
import { ElDialog, ElButton, ElButtonGroup, ElIcon } from "element-plus";
import { Plus, Remove } from "@element-plus/icons-vue";

const props = defineProps({
    modelValue: { type: Boolean, default: false },
    src: { type: String, default: "" }
});
const emit = defineEmits(["update:modelValue"]);

const visible = ref(props.modelValue);
watch(
    () => props.modelValue,
    (v) => {
        visible.value = v;
    }
);
watch(visible, (v) => {
    emit("update:modelValue", v);
});

const scale = ref(1);
const position = reactive({ x: 0, y: 0 });
const isDragging = ref(false);
const dragStart = reactive({ x: 0, y: 0 });
const imageRef = ref(null);

const minScale = 0.2;
const maxScale = 3;
const scaleStep = 0.2;

const containerSize = reactive({ width: 0, height: 0 });
const imageSize = reactive({ width: 0, height: 0 });

const updateSizes = () => {
    if (!imageRef.value) return;
    const container = imageRef.value.parentElement;
    containerSize.width = container.clientWidth;
    containerSize.height = container.clientHeight;
    imageSize.width = imageRef.value.naturalWidth;
    imageSize.height = imageRef.value.naturalHeight;
};

const getBoundaries = () => {
    const scaledWidth = imageSize.width * scale.value;
    const scaledHeight = imageSize.height * scale.value;
    const maxX = Math.max(0, (scaledWidth - containerSize.width) / 2);
    const maxY = Math.max(0, (scaledHeight - containerSize.height) / 2);
    return { minX: -maxX, maxX, minY: -maxY, maxY };
};

const constrainPosition = (x, y) => {
    const b = getBoundaries();
    return {
        x: Math.min(b.maxX, Math.max(b.minX, x)),
        y: Math.min(b.maxY, Math.max(b.minY, y))
    };
};

const zoomIn = () => {
    if (scale.value < maxScale) {
        scale.value = Math.min(scale.value + scaleStep, maxScale);
        const c = constrainPosition(position.x, position.y);
        position.x = c.x;
        position.y = c.y;
    }
};

const zoomOut = () => {
    if (scale.value > minScale) {
        scale.value = Math.max(scale.value - scaleStep, minScale);
        const c = constrainPosition(position.x, position.y);
        position.x = c.x;
        position.y = c.y;
    }
};

const resetZoom = () => {
    scale.value = 1;
    position.x = 0;
    position.y = 0;
};

const handleWheel = (e) => {
    if (e.deltaY < 0) zoomIn();
    else zoomOut();
};

const startDrag = (e) => {
    if (e.target.tagName.toLowerCase() !== "img") return;
    isDragging.value = true;
    dragStart.x = e.clientX - position.x;
    dragStart.y = e.clientY - position.y;
    e.preventDefault();
};

const onDrag = (e) => {
    if (!isDragging.value) return;
    const c = constrainPosition(e.clientX - dragStart.x, e.clientY - dragStart.y);
    position.x = c.x;
    position.y = c.y;
    e.preventDefault();
};

const stopDrag = () => {
    isDragging.value = false;
};

const onImageLoad = () => {
    resetZoom();
    updateSizes();
};
</script>

<style scoped>
.image-preview-container {
    position: relative;
    width: 100%;
    height: 80vh;
    display: flex;
    flex-direction: column;
    align-items: center;
}
.zoom-controls {
    position: absolute;
    top: 10px;
    z-index: 1;
    background-color: rgba(255, 255, 255, 0.9);
    padding: 4px;
    border-radius: 4px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}
.image-container {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    background-color: rgba(0, 0, 0, 0.02);
}
.image-container img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    transition: transform 0.1s ease-out;
    transform-origin: center center;
    user-select: none;
}
</style>
